import amplitude from '@autopass-web/mochi-analytics';

import { environment } from '../environments/environment';

import type { SupportReceiptType } from '../types';
import type { CaptureType, PaymentMethodType } from '@autopass-web/types';
export const AMPLITUDE_KEY = environment.amplitudeKey;

class AnalyticsService {
  constructor() {
    this.init();
  }

  init = () => {
    amplitude.init(environment.amplitudeKey);
  };

  // entry page
  public entryPageView = ({
    chainStorePartnerName,
    chainStoreName,
    transactionTypeI18N,
    captureType,
    amount,
  }: {
    chainStorePartnerName: string;
    chainStoreName: string;
    transactionTypeI18N: string;
    captureType: CaptureType;
    amount: string;
  }) => {
    this._logEvent('交易內容頁_pageview', {
      特店廠商: chainStorePartnerName,
      連鎖店名稱: chainStoreName,
      商店服務類型: transactionTypeI18N,
      支付類型: captureType === 'automatic' ? '當下請款' : '預授權',
      '付款金額/預授權金額': amount,
    });
  };

  public entryTermsOfServiceClick = () => {
    this._logEvent('交易內容頁-服務條款_點擊');
  };

  public entryPrivacyPolicyClick = () => {
    this._logEvent('交易內容頁-隱私權政策_點擊');
  };

  public entryNextButtonClick = ({
    chainStorePartnerName,
    chainStoreName,
    transactionTypeI18N,
    captureType,
  }: {
    chainStorePartnerName: string;
    chainStoreName: string;
    transactionTypeI18N: string;
    captureType: CaptureType;
  }) => {
    this._logEvent('交易內容頁-下一步_點擊', {
      特店廠商: chainStorePartnerName,
      連鎖店名稱: chainStoreName,
      商店服務類型: transactionTypeI18N,
      支付類型: captureType === 'automatic' ? '當下請款' : '預授權',
    });
  };

  public paymentPageView = () => {
    this._logEvent('付款與發票設定頁_pageview');
  };

  public paymentPayClick = ({
    chainStorePartnerName,
    chainStoreName,
    transactionTypeI18N,
    captureType,
    paymentMethod,
    receiptType,
    email,
    checked,
  }: {
    chainStorePartnerName: string;
    chainStoreName: string;
    transactionTypeI18N: string;
    captureType: CaptureType;
    paymentMethod: PaymentMethodType;
    receiptType?: SupportReceiptType;
    email?: string;
    checked?: boolean;
  }) => {
    this._logEvent('付款與發票設定頁-付款_點擊', {
      特店廠商: chainStorePartnerName,
      連鎖店名稱: chainStoreName,
      商店服務類型: transactionTypeI18N,
      支付類型: captureType === 'automatic' ? '當下請款' : '預授權',
      付款方式: paymentMethod,
      會員信箱: email,
      勾選欄狀態: checked ? '已勾選' : '未勾選',
      發票形式: receiptType,
    });
  };

  public paymentErrorPageView = ({
    chainStorePartnerName,
    chainStoreName,
    transactionTypeI18N,
    code,
    message,
    error,
  }: {
    chainStorePartnerName: string;
    chainStoreName: string;
    transactionTypeI18N: string;
    code: string;
    message: string;
    error: unknown;
  }) => {
    this._logEvent('付款與發票設定頁-錯誤訊息_pageview', {
      特店廠商: chainStorePartnerName,
      連鎖店名稱: chainStoreName,
      商店服務類型: transactionTypeI18N,
      顯示錯誤訊息: message,
      失敗原因: message,
      前端error_code: code,
      error_info: JSON.stringify(error),
    });
  };

  public selectPaymentMethodClick = ({
    chainStorePartnerName,
    chainStoreName,
    transactionTypeI18N,
    captureType,
    paymentMethod,
  }: {
    chainStorePartnerName: string;
    chainStoreName: string;
    transactionTypeI18N: string;
    captureType: CaptureType;
    paymentMethod: PaymentMethodType;
  }) =>
    this._logEvent('付款與發票設定頁-選擇付款方式_點擊', {
      特店廠商: chainStorePartnerName,
      連鎖店名稱: chainStoreName,
      商店服務類型: transactionTypeI18N,
      支付類型: captureType === 'automatic' ? '當下請款' : '預授權',
      付款方式: paymentMethod,
    });

  public selectReceiptTypeClick = ({
    chainStorePartnerName,
    chainStoreName,
    transactionTypeI18N,
    captureType,
    receiptType,
  }: {
    chainStorePartnerName: string;
    chainStoreName: string;
    transactionTypeI18N: string;
    captureType: CaptureType;
    receiptType: SupportReceiptType;
  }) => {
    this._logEvent('付款與發票設定頁-選擇發票開立方式_點擊', {
      特店廠商: chainStorePartnerName,
      連鎖店名稱: chainStoreName,
      商店服務類型: transactionTypeI18N,
      支付類型: captureType === 'automatic' ? '當下請款' : '預授權',
      發票形式: receiptType,
    });
  };
  public redirectPaymentLinkLinePay = () =>
    this._logEvent('付款與發票設定頁_LinePayUrl');
  public redirectPaymentLinkEasyWallet = () =>
    this._logEvent('付款與發票設定頁_EasyWalletUrl');
  public redirectPaymentLinkAutopass = () =>
    this._logEvent('付款與發票設定頁_CarMochiUrl');
  public clickAddSpace = (params: {
    chain_store_name: string;
    bulletin_board_code: string;
    bulletin_id: string;
  }) =>
    this._logEvent('交易結果頁_交易成功_廣告版位_點擊', {
      場站名稱: params.chain_store_name,
      bulletin_board_code: params.bulletin_board_code,
      廣告id: params.bulletin_id,
    });

  public entryAdSpaceClick = (params: {
    chain_store_name: string;
    bulletin_board_code: string;
    bulletin_id: string;
  }) =>
    this._logEvent('交易內容_廣告版位_點擊', {
      場站名稱: params.chain_store_name,
      bulletin_board_code: params.bulletin_board_code,
      廣告id: params.bulletin_id,
    });

  // event logger
  private _logEvent = (
    eventName: string,
    data: Record<string, unknown> = {},
  ) => {
    amplitude.log(eventName, data);
  };

  public logEvent = this._logEvent;
}

const analyticsService = new AnalyticsService();

export { analyticsService };
